/**
 * Footnotes Context
 *
 * Manages footnotes for a page with the following features:
 * - Loads footnotes from API and tracks them by internalId (from sk)
 * - Orders footnotes by footnote_no (null values at start)
 * - Tracks metadata: isNewRow, isModified, isDeleted
 * - Provides updateFootnotes method for modal to commit changes
 */

import { ExcelRow } from '@amzn/gft-ag-grid-enterprise';
import { usePageContext } from '@context/PageContext.tsx';
import useGetFootnotesForPage from '@services/api/deckManagement/hooks/useFootnotes';
import { entityKeyAsId } from '@utils/deckHelpers';
import { convertFootnoteNumberToSuperscript } from '@utils/footnoteHelpers';
import { createContext, use, useCallback, useEffect, useState } from 'react';
import {
  EnhancedFootnote,
  FootnotesContextType,
  FootnotesProviderProps,
  SelectedHeaderFootnotes,
} from 'types/FootnotesContext.types';

/**
 * Sort footnotes by footnote_no (null values first)
 * @param footnotes - Array of footnotes to sort
 * @returns Sorted array
 */
const sortFootnotes = (footnotes: EnhancedFootnote[]): EnhancedFootnote[] => {
  return [...footnotes].sort((a, b) => {
    const numA = a.footnote_no ? parseInt(a.footnote_no, 10) : null;
    const numB = b.footnote_no ? parseInt(b.footnote_no, 10) : null;

    if (numA === null) return -1;
    if (numB === null) return 1;
    return numA - numB;
  });
};

export const FootnotesContext = createContext<FootnotesContextType | undefined>(undefined);

/**
 * Footnote Provider Component
 *
 * Provides footnotes context to the application, managing footnote state
 * and providing methods for updating footnotes from the modal.
 *
 * @param props - Provider props containing children
 */
export const FootnoteProvider = ({ children }: FootnotesProviderProps) => {
  const [footnotes, setFootnotes] = useState<EnhancedFootnote[]>([]);
  const [displayFootnotesModal, setDisplayFootnotesModal] = useState<boolean>(false);
  const [selectedHeaderFootnotes, setSelectedHeaderFootnotes] = useState<SelectedHeaderFootnotes | null>(null);

  const { currentGranularityPage } = usePageContext();
  const currentPageId = currentGranularityPage?.pk ? entityKeyAsId(currentGranularityPage.pk) : '';
  const { data: footnotesData, isLoading, isError, error } = useGetFootnotesForPage(currentPageId);

  // Load and enhance footnotes from API
  useEffect(() => {
    if (footnotesData) {
      const enhancedFootnotes: EnhancedFootnote[] = footnotesData.map((footnote) => ({
        ...footnote,
        metadata: {
          internalId: entityKeyAsId(footnote.sk),
          isNewRow: false,
          isModified: false,
          isDeleted: false,
        },
      }));

      setFootnotes(sortFootnotes(enhancedFootnotes));
    }
  }, [footnotesData]);

  /**
   * Update footnotes from modal after save
   * Merges changes with existing footnotes, maintaining metadata
   *
   * @param updatedFootnotes - Updated footnotes array from modal
   */
  const updateFootnotes = useCallback((updatedFootnotes: EnhancedFootnote[]) => {
    setFootnotes(sortFootnotes(updatedFootnotes));
  }, []);

  /**
   * Generate footnote changeset for SaveDeck
   * @param originalFootnotes - Original footnotes from API
   * @param modifiedFootnotes - Modified footnotes with metadata
   * @returns Changeset with create, update, delete arrays
   */
  const generateFootnoteChangeset = useCallback(() => {
    const createdFootnotes: EnhancedFootnote[] = [];
    const updatedFootnotes: EnhancedFootnote[] = [];
    const deletedFootnotes: { footnote_id: string; page_id: string }[] = [];

    footnotes.forEach((footnote) => {
      const baseFootnote = {
        footnote_id: footnote.metadata.internalId,
        page_id: currentPageId,
      };
      if (footnote.metadata.isDeleted) {
        deletedFootnotes.push(baseFootnote);
      } else if (footnote.metadata.isNewRow) {
        createdFootnotes.push({
          ...baseFootnote,
          ...footnote,
        });
      } else if (footnote.metadata.isModified) {
        updatedFootnotes.push({
          ...baseFootnote,
          ...footnote,
        });
      }
    });

    return { createdFootnotes, updatedFootnotes, deletedFootnotes };
  }, [footnotes, currentPageId]);

  /**
   * Format footnotes for export to Excel
   * @returns Formatted Excel rows or undefined
   */
  const getFormattedFootnotesForExport = useCallback(
    (mergeAcross?: number): ExcelRow[] | undefined => {
      return footnotes?.map((footnote) => {
        return {
          cells: [
            {
              data: {
                value: '', // Ensures 1st column with Row number is not used
                type: 'String',
              },
            },
            {
              data: {
                value: `${convertFootnoteNumberToSuperscript(footnote.footnote_no || '1')} ${footnote.footnote_description}`,
                type: 'String',
              },
              ...(mergeAcross && { mergeAcross }),
              styleId: 'excel-footnote-item',
            },
          ],
        };
      });
    },
    [footnotes],
  );

  const value: FootnotesContextType = {
    footnotes,
    displayFootnotesModal,
    setDisplayFootnotesModal,
    selectedHeaderFootnotes,
    setSelectedHeaderFootnotes,
    updateFootnotes,
    generateFootnoteChangeset,
    getFormattedFootnotesForExport,
    isLoading,
    isError,
    error,
  };

  return <FootnotesContext value={value}>{children}</FootnotesContext>;
};

/**
 * Hook to access footnotes context
 * @throws Error if used outside FootnoteProvider
 * @returns Footnotes context value
 */
export const useFootnotesContext = (): FootnotesContextType => {
  const context = use(FootnotesContext);
  if (context === undefined) {
    throw new Error('useFootnotesContext must be used within a FootnoteProvider');
  }
  return context;
};
